import React, { FC, useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { Grid, IconButton, Typography, Button, Modal } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import SecondaryTitleBar from 'components/secondary-title-bar/secondary-title-bar';
import { Field } from 'redux-form';
import { renderTextArea } from 'components/form/field/redux-field';
import { required } from 'components/form/validation/validation';
import moment from 'moment';
import { EditHistory } from 'components/icons/icons';
import { getModalStyle } from 'services/utils/styles-service';
import { useSelector } from 'react-redux';
import { IState } from 'interfaces/redux/IState';
import { CSL } from 'constants/index';
import RichTextEditor, { StandardFeatures, EditorValueFormat } from '../rich-text-editor';
import { IndexedDrugSections, IProps, EditableSection } from './interfaces';
import { styles } from './styles';
import TherapySubTitle from '../therapy-subtitle/therapy-subtitle';
import { EducationSubsection, CounselingTaskType } from '../../constants/enums';
import History from './text-section-history';
import HTTP from '../../services/http';

const EditableTextSection: FC<IProps> = (props: IProps): JSX.Element => {
  const { classes } = props;
  const noteFooter = 'This is a progress note for the goals of therapy';

  // Holding the editing status of the main content and sections
  const [editingContent, setEditingContent] = useState<boolean>(false);
  const [editingSecondaryContent, setEditingSecondaryContent] = useState<boolean>(false);
  const [editingSections, setEditingSections] = useState<IndexedDrugSections<boolean>>({});

  // Holding the content of the main and secondary content
  const [content, setContent] = useState<string | undefined>(props.initialContent);
  const [secondaryContent, setSecondaryContent] = useState<string | undefined>(
    props.initialSecondaryContent,
  );

  const [contentNeedsUpdate, setContentNeedsUpdate] = useState<boolean>(false);
  const [secondaryContentNeedsUpdate, setSecondaryContentNeedsUpdate] = useState<boolean>(false);
  const [sectionContentNeedsUpdate, setSectionContentNeedsUpdate] = useState<
    IndexedDrugSections<boolean>
  >({});
  const [editHistoryModalOpen, setEditHistoryModalOpen] = useState(false);
  const tasks = useSelector((state: IState) => state.tasks.data) || [];
  const ptgotNoteRef = React.useRef<string>('');
  const ptgotNoteAddedByRef = React.useRef<string>('');
  const ptgotNoteHistoryRef = React.useRef<{ content: string; added: string }[]>([]);
  const ptgotlastNoteFromInitialTaskRef = React.useRef<string>('');

  React.useEffect(() => {
    setContent(props.initialContent);
  }, [props.initialContent]);

  React.useEffect(() => {
    setSecondaryContent(props.initialSecondaryContent);
  }, [props.initialSecondaryContent]);

  React.useEffect(() => {
    if (props.saveContentUpdatesTrigger) {
      setContentNeedsUpdate(true);
      setSecondaryContentNeedsUpdate(true);
      // setSectionContentNeedsUpdate with all drugs, all sections
      const drugSubSectionKeys = Object.keys(props.drugSubSections ?? {});
      const allSectionsNeedingUpdate: IndexedDrugSections<boolean> = {};
      if (drugSubSectionKeys.length > 0) {
        drugSubSectionKeys.forEach((drugKey, drugIndex) => {
          Object.keys(props.drugSubSections?.[drugKey] ?? {}).forEach(
            (_subSectionKey, subSectionIndex) => {
              if (allSectionsNeedingUpdate[drugIndex] === undefined) {
                allSectionsNeedingUpdate[drugIndex] = {};
              }
              allSectionsNeedingUpdate[drugIndex][subSectionIndex] = true;
            },
          );
        });
        setSectionContentNeedsUpdate(allSectionsNeedingUpdate);
      }
      props.onFinishedSaving();
    }
  }, [props.saveContentUpdatesTrigger]);

  const toggleEdit = () => {
    switch (props.editSection) {
      case EditableSection.None:
        break;
      case EditableSection.Primary:
        toggleEditingContent();
        break;
      case EditableSection.Secondary:
        toggleEditingSecondaryContent();
        break;
      case EditableSection.Both:
        toggleEditingContent();
        toggleEditingSecondaryContent();
        break;
      default:
        break;
    }
  };

  const toggleEditingContent = () => {
    setEditingContent(!editingContent);
  };

  const toggleEditingSecondaryContent = () => {
    setEditingSecondaryContent(!editingSecondaryContent);
  };

  const toggleEditingSection = (drugIndex: number, subSectionIndex: number) => {
    const currentValue = editingSections?.[drugIndex]?.[subSectionIndex];
    setEditingSections(prevValue => {
      return {
        ...prevValue,
        [drugIndex]: {
          [subSectionIndex]: !currentValue,
        },
      };
    });
  };

  const updateContent = (content: string) => {
    if (props.onChange) {
      props.onChange(props.title, content);
    }
    setContentNeedsUpdate(false);
  };

  const updateSecondaryContent = (content: string) => {
    if (props.onChange) {
      props.onChange(props.title, undefined, content);
    }
    setSecondaryContentNeedsUpdate(false);
  };

  const updateDrugSubsectionContent = (
    drugIndex: number,
    subSectionIndex: number,
    content: string,
  ) => {
    props.onChange(props.title, content, undefined, {
      drugIndex,
      subSectionIndex,
    });

    setSectionContentNeedsUpdate(prevValue => {
      return {
        ...prevValue,
        [drugIndex]: {
          [subSectionIndex]: false,
        },
      };
    });
  };

  const isJsonArrayString = (str: string): boolean => {
    try {
      const parsed = JSON.parse(str);
      return Array.isArray(parsed);
    } catch (e) {
      return false;
    }
  };

  const getFormattedContent = (content: string): string => {
    const newNote = {
      content: content,
      added: `${moment.utc(new Date()).format('MM/DD/YYYY hh:mm A')} by ${props.userName || ''}`,
    };
    const tempLastItem = ptgotNoteHistoryRef.current[ptgotNoteHistoryRef.current.length - 1];
    ptgotNoteHistoryRef.current[tempLastItem ? ptgotNoteHistoryRef.current.length - 1 : 0] =
      newNote;
    return JSON.stringify(ptgotNoteHistoryRef.current);
  };

  const getLastNoteFromInitialCslTask = React.useMemo((): string | undefined => {
    const tempTaskIdNumber = (props.qaId?.split('-') || []).pop();
    if (props.currentCslType === CounselingTaskType.Ongoing && tempTaskIdNumber !== 'undefined') {
      const tempTaskId = `${CSL}${tempTaskIdNumber}`;
      const selectedTask = tasks[tempTaskId];
      if (
        selectedTask &&
        Object.keys(selectedTask).length > 0 &&
        selectedTask.status_category_id &&
        [1, 2].includes(selectedTask.status_category_id)
      ) {
        const tempInitialCslTasks =
          Object.values(tasks)
            .filter(
              (task: any) =>
                task.therapy_id === selectedTask.therapy_id &&
                task.taskType === CSL &&
                task.counseling_type === CounselingTaskType.Initial &&
                task.status_category_id &&
                [3, 4].includes(task.status_category_id),
            )
            .sort((a: any, b: any) => b.id - a.id) || [];
        if (tempInitialCslTasks.length > 0) {
          const tempInitialCslTask = tempInitialCslTasks[0];
          HTTP.get(`tasks/CSL/${tempInitialCslTask.id}/snapshot`)
            .then((response: any) => {
              const res = response.data;
              if (response?.data?.task_snapshot) {
                const tempSnapshot = JSON.parse(res.task_snapshot);
                const sectionsInfo = Object.values(tempSnapshot?.protocolData) || [];
                if (sectionsInfo.length > 0) {
                  const firstSection = sectionsInfo[0] as any;
                  const initialCslProgressNotes =
                    firstSection.dotPhrase?.[EducationSubsection.ExpectationsGoals];
                  if (isJsonArrayString(initialCslProgressNotes)) {
                    const tempExistingNotes = JSON.parse(initialCslProgressNotes);
                    if (tempExistingNotes.length > 0) {
                      const latestNote = tempExistingNotes[tempExistingNotes.length - 1];
                      ptgotNoteAddedByRef.current = latestNote.added ? `${latestNote.added}` : '';
                      return (ptgotlastNoteFromInitialTaskRef.current = latestNote.content || '');
                    }
                  }
                  ptgotlastNoteFromInitialTaskRef.current = initialCslProgressNotes;
                  return ptgotlastNoteFromInitialTaskRef.current;
                }
                return undefined;
              }
              return undefined;
            })
            .catch((err: any) => {
              ptgotlastNoteFromInitialTaskRef.current = '';
              return undefined;
            });
        }
      }
    }
    ptgotlastNoteFromInitialTaskRef.current = '';
    return undefined;
  }, [props.qaId, props.currentCslType, tasks]);

  const getLatestPtgotNoteContent = (content: string): string => {
    if (isJsonArrayString(content)) {
      const tempExistingNotes = JSON.parse(content);
      if (tempExistingNotes.length > 0) {
        if (tempExistingNotes.length > 1) {
          ptgotNoteHistoryRef.current = tempExistingNotes;
        }
        const latestNote = tempExistingNotes[tempExistingNotes.length - 1];
        ptgotNoteAddedByRef.current = latestNote.added ? `${latestNote.added}` : '';
        return latestNote.content || '';
      }
    }
    if (ptgotlastNoteFromInitialTaskRef.current) {
      return ptgotlastNoteFromInitialTaskRef.current;
    }
    return content;
  };

  const getFinalContentForPtgot = (content: string, ptgotNoteContent: string): string => {
    const newNote = {
      content: ptgotNoteContent,
      added: `${moment.utc(new Date()).format('MM/DD/YYYY hh:mm A')} by ${props.userName || ''}`,
    };
    ptgotNoteHistoryRef.current = [...ptgotNoteHistoryRef.current, newNote];
    return JSON.stringify(ptgotNoteHistoryRef.current);
  };

  const handleEditHistoryModal = (e: any) => {
    e.stopPropagation();
    setEditHistoryModalOpen(!editHistoryModalOpen);
  };

  const renderDrugSubsection = (
    subSection: { name: string; content: string | undefined },
    drugIndex: number,
    subSectionIndex: number,
  ): JSX.Element => {
    const docReadOnly =
      !props.editing &&
      (!editingSections ||
        !editingSections[drugIndex] ||
        editingSections[drugIndex][subSectionIndex] !== true);

    return (
      <Grid container className={classes.subSectionWrapper} justifyContent="space-between">
        <Typography className={classes.subSectionTitle}>{subSection.name}</Typography>
        {docReadOnly && !props.viewOnly && props.showEditButton && (
          <IconButton
            data-qa-id={`${props.qaId}-subsection-${
              subSection?.name?.replace(/\s/g, '') || ''
            }-edit-button`}
            onClick={() => toggleEditingSection(drugIndex, subSectionIndex)}
            size="large"
          >
            <EditIcon className={classes.editIcon} fontSize="small" />
          </IconButton>
        )}
        <>
          <Grid item xs={12}>
            <RichTextEditor
              qaId={`${props.qaId}-subsection-${subSection?.name?.replace(/\s/g, '') || ''}-editor`}
              autoFocus={false}
              features={StandardFeatures}
              featuresBar
              hideWrapper
              initialValue={
                subSection.name === EducationSubsection.ExpectationsGoals
                  ? getLatestPtgotNoteContent(subSection.content || '')
                  : subSection.content
              }
              initialValueFormat={EditorValueFormat.Html}
              outputFormat={EditorValueFormat.Html}
              placeHolder=""
              readOnly={docReadOnly}
              onSaveContentUpdates={content => {
                const isExpectationsGoals =
                  subSection.name === EducationSubsection.ExpectationsGoals;
                const ptgotNoteContent = ptgotNoteRef.current || '';
                const finalContent =
                  isExpectationsGoals && ptgotNoteRef.current
                    ? getFinalContentForPtgot(content || '', ptgotNoteContent)
                    : isExpectationsGoals
                    ? getFormattedContent(content)
                    : content;
                updateDrugSubsectionContent(drugIndex, subSectionIndex, finalContent);
              }}
              saveContentUpdatesTrigger={sectionContentNeedsUpdate?.[drugIndex]?.[subSectionIndex]}
              cancelContentUpdatesTrigger={props.cancelContentUpdatesTrigger}
            />
          </Grid>
          {!props.editing &&
            props.ptgotAvailable &&
            props.ptgotAvailable === true &&
            subSection.name === EducationSubsection.ExpectationsGoals &&
            props.currentCslType &&
            [CounselingTaskType.Initial, CounselingTaskType.Ongoing].includes(
              props.currentCslType,
            ) && (
              <>
                <Typography
                  sx={{
                    marginTop: '0px',
                  }}
                >
                  {ptgotNoteAddedByRef.current ? (
                    <>
                      <span>
                        <br />
                        {ptgotNoteAddedByRef.current}
                      </span>
                      <br />
                      <span>{noteFooter}</span>
                      {ptgotNoteHistoryRef.current.length > 0 && (
                        <span>
                          <br />
                          <Button onClick={handleEditHistoryModal} sx={{ ml: -2 }}>
                            <EditHistory />
                          </Button>
                        </span>
                      )}
                    </>
                  ) : (
                    <span>
                      <br />
                      {noteFooter}
                    </span>
                  )}
                </Typography>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      marginTop: '20px',
                    }}
                  >
                    Add Progress Towards Goals of Therapy note *
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="ptgot_note"
                    label=""
                    placeholder="Add Progress Note"
                    component={renderTextArea}
                    multiline
                    rows={3}
                    id="ptgot_note"
                    width="100%"
                    validate={[required]}
                    onChange={(_e: any, newValue: string) => {
                      ptgotNoteRef.current = newValue;
                    }}
                  />
                </Grid>
                <Modal open={editHistoryModalOpen} onClose={handleEditHistoryModal}>
                  <div style={getModalStyle()} className={classes.Modal}>
                    <History ptgotNoteHistory={ptgotNoteHistoryRef.current} />
                  </div>
                </Modal>
              </>
            )}
        </>
      </Grid>
    );
  };

  const renderPrimaryAndSecondarySections = () => {
    const renderPrimaryContent =
      ((props.viewOnly || props.readOnly) && content) ||
      (!(props.viewOnly || props.readOnly) && content !== undefined);

    const renderSecondaryContent =
      ((props.viewOnly || props.readOnly) && secondaryContent) ||
      (!(props.viewOnly || props.readOnly) && secondaryContent !== undefined);

    const hasRightSideComponent = Boolean(props.rightSideComponent);
    const primaryEditable =
      props.editSection != null &&
      [EditableSection.Both, EditableSection.Primary].includes(props.editSection);
    const secondaryEditable =
      props.editSection != null &&
      [EditableSection.Both, EditableSection.Secondary].includes(props.editSection);

    return (
      <Grid container>
        {/* Primary */}
        <Grid item xs={hasRightSideComponent ? 6 : 12}>
          {renderPrimaryContent && (
            <RichTextEditor
              qaId={`${props.qaId}-editor`}
              autoFocus
              features={StandardFeatures}
              featuresBar
              initialValue={content}
              initialValueFormat={EditorValueFormat.Html}
              outputFormat={EditorValueFormat.Html}
              placeHolder=""
              readOnly={(!editingContent && !props.editing) || !primaryEditable}
              onSaveContentUpdates={content => updateContent(content)}
              saveContentUpdatesTrigger={contentNeedsUpdate}
              cancelContentUpdatesTrigger={props.cancelContentUpdatesTrigger}
            />
          )}
        </Grid>
        {/* Right Side */}
        {hasRightSideComponent && (
          <Grid item xs={6} className={classes.rightSideComponentWrapper}>
            {props.rightSideComponent}
          </Grid>
        )}
        {/* Secondary */}
        <Grid item xs={12}>
          {renderSecondaryContent && props.editSection !== EditableSection.None && (
            <RichTextEditor
              qaId={`${props.qaId}-editor`}
              autoFocus
              features={StandardFeatures}
              featuresBar
              initialValue={secondaryContent}
              initialValueFormat={EditorValueFormat.Html}
              onSaveContentUpdates={content => updateSecondaryContent(content)}
              saveContentUpdatesTrigger={secondaryContentNeedsUpdate}
              outputFormat={EditorValueFormat.Html}
              placeHolder=""
              readOnly={(!editingSecondaryContent && !props.editing) || !secondaryEditable}
              cancelContentUpdatesTrigger={props.cancelContentUpdatesTrigger}
            />
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={2}>
      <SecondaryTitleBar
        title={props.title}
        button={
          !editingContent &&
          content &&
          !props.viewOnly &&
          !props.readOnly &&
          props.showEditButton ? (
            <IconButton data-qa-id={`${props.qaId}-edit-button`} onClick={toggleEdit} size="large">
              <EditIcon className={classes.editIcon} fontSize="small" />
            </IconButton>
          ) : null
        }
      />

      <Grid item xs={12}>
        {renderPrimaryAndSecondarySections()}
        {props.drugSubSections &&
          Object.keys(props.drugSubSections).map((key, drugIndex) => {
            return (
              <>
                {/* drug label */}
                <TherapySubTitle label={key} />
                {/* editable sub sections */}
                {props.drugSubSections &&
                  props.drugSubSections[key] &&
                  Object.keys(props.drugSubSections[key]).map((subSectionKey, subSectionIndex) => {
                    return renderDrugSubsection(
                      { name: subSectionKey, content: props.drugSubSections?.[key][subSectionKey] },
                      drugIndex,
                      subSectionIndex,
                    );
                  })}
              </>
            );
          })}
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(EditableTextSection);
